import { useMemo } from 'preact/hooks';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartPalette } from '../../themes/chartThemeColors';
import type { PieChartDataItem } from '../../types/charts';
import { EChart, type AppEChartOption } from './EChart';

interface CompositionDonutChartProps {
  data: PieChartDataItem[];
  colors?: string[];
  centerValue?: string | number;
  centerLabel?: string;
  className?: string;
}

interface DonutTooltipParam {
  color?: string;
  name?: string;
  value?: number;
  percent?: number;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

export function CompositionDonutChart({
  data,
  colors,
  centerValue,
  centerLabel,
  className = '',
}: CompositionDonutChartProps) {
  const { theme } = useTheme();

  const option = useMemo<AppEChartOption>(() => {
    const chartColors = colors ?? getChartPalette(theme);

    return {
      animation: true,
      animationDuration: 450,
      animationEasing: 'cubicOut',
      aria: {
        enabled: true,
        description: `Portfolio composition chart with ${data.length} ${data.length === 1 ? 'group' : 'groups'}.`,
        decal: { show: false },
      },
      color: chartColors,
      tooltip: {
        trigger: 'item',
        confine: true,
        backgroundColor: theme.colors.surface,
        borderColor: theme.colors.border,
        borderWidth: 1,
        padding: [10, 12],
        textStyle: {
          color: theme.colors.text,
          fontFamily: theme.typography.fontFamily,
          fontSize: 12,
        },
        extraCssText: `border-radius: ${theme.borderRadius.md}; box-shadow: ${theme.shadows.md};`,
        formatter: (rawParam: unknown) => {
          const param = rawParam as DonutTooltipParam;
          const color = typeof param.color === 'string' ? param.color : theme.colors.primary;
          const name = escapeHtml(param.name ?? '');
          const value = Number(param.value ?? 0).toLocaleString(undefined, {
            maximumFractionDigits: 0,
          });
          const percentage = Number(param.percent ?? 0).toFixed(1).replace('.0', '');

          return `<div class="echart-tooltip echart-tooltip--donut"><div class="echart-tooltip__row"><span class="echart-tooltip__dot" style="background:${escapeHtml(color)}"></span><span>${name}</span><strong>${escapeHtml(value)} · ${percentage}%</strong></div></div>`;
        },
      },
      series: [
        {
          name: 'Portfolio composition',
          type: 'pie',
          radius: ['68%', '92%'],
          center: ['50%', '50%'],
          avoidLabelOverlap: true,
          minAngle: 2,
          padAngle: data.length > 1 ? 1.5 : 0,
          selectedMode: false,
          silent: false,
          label: { show: false },
          labelLine: { show: false },
          itemStyle: {
            borderColor: theme.colors.surface,
            borderWidth: data.length > 1 ? 2 : 0,
            borderRadius: 4,
          },
          emphasis: {
            scale: true,
            scaleSize: 4,
            itemStyle: {
              shadowBlur: 12,
              shadowColor: 'rgba(0, 0, 0, 0.16)',
            },
          },
          data: data.map((item) => ({
            name: item.category,
            value: item.value,
          })),
        },
      ],
    };
  }, [data, colors, theme]);

  return (
    <div className={`composition-donut-echart ${className}`.trim()}>
      <EChart
        option={option}
        ariaLabel="Portfolio composition"
        empty={data.length === 0}
        emptyMessage="No composition data"
      />
      {(centerValue !== undefined || centerLabel) && (
        <div className="pie-chart-center" aria-hidden="true">
          {centerValue !== undefined && (
            <span className="pie-chart-center__value">{centerValue}</span>
          )}
          {centerLabel && <span className="pie-chart-center__label">{centerLabel}</span>}
        </div>
      )}
    </div>
  );
}
